import { defineStore } from 'pinia'
import api from '@/store/api'

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    groups: [],
    watchlists: [],
    emailAlerts: [],
    trackedBacktests: [],
    insiderAlerts: [],
    whaleAlerts: [],
    limit: 5,
    isTeamMember: false,
    isSubscriber: false,
    isProSubscriber: false,
    isEnterpriseSubscriber: false,
    tradingModels: [],
    sharedModels: [],
    tradingModelSummaries: [],
    screens: [],
    socket_consumer: null,
    socket_channel: null,
    socket_callback: null,
    trading_model_update: null,
    screen_update: null,
    status_update_snackbar: false,
    active_stocks: [],
    active_filers: [],
    trending_stocks: [],
    trending_filers: [],
    backtest_socket_channel: null,
    backtest_socket_callback: null,
    isLoggedIn: false
  }),

  getters: {
    subscription: state => {
      if (state.isEnterpriseSubscriber) return 'ENTERPRISE'
      if (state.isProSubscriber) return 'PRO'
      if (state.isSubscriber) return 'STANDARD'
      return null
    }
  },

  actions: {
    setGroups(groups) {
      function compare(a, b) {
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
      }
      groups.forEach(bt => {
        bt.filers.sort(compare)
      })
      this.groups = groups
    },

    setWatchlists(groups) {
      function compare(a, b) {
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
      }
      if (!groups) return
      if (groups === 'You are not logged in') return
      groups.forEach(bt => {
        bt.stocks.sort(compare)
      })
      this.watchlists = groups
    },

    setEmailAlerts(alerts) {
      this.emailAlerts = alerts
    },

    setMostActiveStocks(alerts) {
      this.active_stocks = alerts
    },

    setMostActiveFilers(alerts) {
      this.active_filers = alerts
    },

    setTrendingStocks(alerts) {
      this.trending_stocks = alerts
    },

    setTrendingFilers(alerts) {
      this.trending_filers = alerts
    },

    setBacktests(backtests) {
      function extractMonthlyPerformance(bt, data) {
        if (!data || data.length === 0) return
        bt.monthly_performance = []
        let whale = data[0]
        let sp = data[1]

        for (let k = 0; k < whale.values.length; k++) {
          let d = whale.values[k]
          let e = sp.values[k]
          bt.monthly_performance.push({
            month: d.x,
            whale: d.z,
            sp: e.z,
            diff: d.z - e.z
          })
        }
      }

      backtests.forEach(bt => {
        extractMonthlyPerformance(bt, JSON.parse(bt.chart_data))
        bt.last_portfolio = JSON.parse(bt.last_portfolio)
        if (!bt.last_portfolio) bt.last_portfolio = []

        bt.criteria = JSON.parse(bt.criteria_json)
      })

      this.trackedBacktests = backtests
    },

    setUser(user) {
      if (!user) return

      this.user = user
      if (user.id && user.id > 0) this.isLoggedIn = true

      const today = new Date()
      const yesterday = today.setDate(today.getDate() - 1)

      if (
        user.subscription_limit &&
        (user.subscription_limit.allowed_team_members > 0 || user.primary_account)
      ) {
        this.isTeamMember = true
      }

      if (user.user_subscription) {
        let date = new Date(user.user_subscription.subscription_end_date)
        this.isSubscriber = date >= yesterday
      }

      if (this.isSubscriber) {
        this.limit = 20
        if (user.user_subscription && user.user_subscription.subscription_type_id === 2) {
          this.isProSubscriber = true
          this.limit=50
        } else if (user.user_subscription && user.user_subscription.subscription_type_id === 3) {
          this.isEnterpriseSubscriber = true
          this.limit = 9999
        }
        if (user.subscription_limit) {
          this.limit = user.subscription_limit.report_filer_limit
        }
      }

      if (user.primary_account) {
        let date = new Date(user.primary_account.user_subscription.subscription_end_date)
        this.isSubscriber = date >= yesterday

        if (this.isSubscriber) {
          if (user.primary_account.subscription_limit) {
            this.limit = user.primary_account.subscription_limit.report_filer_limit
          }
          if (user.primary_account.user_subscription.subscription_type_id === 2) {
            this.isProSubscriber = true
          } else if (user.primary_account.user_subscription.subscription_type_id === 3) {
            this.isEnterpriseSubscriber = true
            this.limit = 9999
          }
        } else {
          this.limit = 5
          this.isEnterpriseSubscriber = false
          this.isProSubscriber = false
        }
      }
      user.limit = this.limit
    },

    setInsiderAlerts(insiderAlerts) {
      insiderAlerts.forEach(alert => {
        let criteria = JSON.parse(alert.criteria_body)
        let cluster = criteria.alert_criteria.entry_cluster_buy_criteria

        let transactionType =
          cluster.transaction_type === 1 ? 'All Buy Transactions' : 'Open Market Purchases'

        let dateRange =
          cluster.date_range === 0
            ? 'Past Day'
            : cluster.date_range === 1
              ? 'Past 3 Days'
              : cluster.date_range === 2
                ? 'Past 7 Days'
                : cluster.date_range === 3
                  ? 'Past Month'
                  : cluster.date_range === 4
                    ? 'Past 3 Months'
                    : 'Past 2 Weeks'

        let ownerTypes = (() => {
          if (!cluster.owner_types.length || cluster.owner_types.length === 5) {
            return ['All']
          } else {
            return cluster.owner_types.map(type =>
              type === 1
                ? '>10% Owner'
                : type === 2
                  ? 'Director'
                  : type === 3
                    ? 'Officer'
                    : type === 4
                      ? 'C-Level Officer'
                      : 'Other Owner'
            )
          }
        })()

        alert.strategy = {
          transactionType,
          dateRange,
          ownerTypes
        }
      })

      this.insiderAlerts = insiderAlerts
    },

    setTradingModels(tradingModels) {
      this.tradingModels = tradingModels
    },

    setSharedModels(sharedModels) {
      this.sharedModels = sharedModels
    },

    setScreens(screens) {
      this.screens = screens
    },

    // API Actions
    async loadUser() {
      try {
        const res = await api.get('user.json')
        if (res.data.id) this.setUser(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadGroups() {
      try {
        const res = await api.get('groups.json?isArray=1')
        if (res.data) this.setGroups(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadWatchlists() {
      try {
        const res = await api.get('watchlists.json?isArray=1')
        if (res.data) this.setWatchlists(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadEmailAlerts() {
      try {
        const res = await api.get('email_alert/email_alerts2.json')
        if (res.data) this.setEmailAlerts(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadTrackedBacktests() {
      try {
        const res = await api.get('backtest_tracking.json?isArray=true')
        if (res.data) this.setBacktests(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadInsiderAlerts() {
      try {
        const res = await api.get('insider_alerts.json')
        this.setInsiderAlerts(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadTradingModels() {
      try {
        const res = await api.get('trading_models.json')
        this.setTradingModels(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadSharedModels() {
      try {
        const res = await api.get('shared_models.json')
        this.setSharedModels(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadScreens() {
      try {
        const res = await api.get('screens.json')
        this.setScreens(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadMostActiveStocks() {
      try {
        const res = await api.get('most_active_stocks.json')
        this.setMostActiveStocks(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadMostActiveFilers() {
      try {
        const res = await api.get('most_active_filers.json')
        this.setMostActiveFilers(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadTrendingStocks() {
      try {
        const res = await api.get('trending_stocks.json')
        this.setTrendingStocks(res.data)
      } catch (error) {
        console.log(error)
      }
    },

    async loadTrendingFilers() {
      try {
        const res = await api.get('trending_filers.json')
        this.setTrendingFilers(res.data)
      } catch (error) {
        console.log(error)
      }
    }
  }
})
